import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BASE_URL } from "../../config/axios";
import "./BdDashboardinner.css";
import BarChartComponent from "../BarChart/BarChartComponent";
// --- Type Definitions ---
const BdDashboardinner = () => {
  // State for date range (default: last 7 days, but data represents "last day" as requested)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const categories = [
    "Highway",
    "Railway",
    "Water",
    "Metro",
    "Urban Infrastructure Buildings",
  ];

  const states = [
    "Andaman and Nicobar Islands",
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chandigarh",
    "Chhattisgarh",
    "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jammu and Kashmir",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Ladakh",
    "Lakshadweep",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Puducherry",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  // Filter states for Active Tenders box
  const [sectorFilter, setSectorFilter] = useState("null");
  const [tenderTypeFilter, setTenderTypeFilter] = useState("null");
  const [stateFilter, setStateFilter] = useState("null");

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    activeCounts: {
      newTenders: 0,
      wishListTenders: 0,
      targetTenders: 0,
      finalTenders: 0,
      bidPreparation: 0,
      submittedTenders: 0,
    },
    inactiveCounts: {
      closed: 0,
      expired: 0,
    },
    sectorChart: [],
    tenderTypeChart: [],
    bgUpcoming: 0,
    bgCreated: 0,
    bgRequiredCount: 0,
  });

  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Generate dummy data based on date range and filters
  const HARDCODED_DUMMY_DATA = {
    activeCounts: {
      newTenders: 24,
      wishListTenders: 18,
      targetTenders: 32,
      finalTenders: 15,
      bidPreparation: 12,
      submittedTenders: 27,
    },
    inactiveCounts: {
      closed: 45,
      expired: 28,
    },
    sectorChart: [
      { sector: "Highway", count: 52 },
      { sector: "Railway", count: 38 },
      { sector: "Water", count: 61 },
      { sector: "Metro", count: 29 },
      { sector: "Urban Infrastructure Buildings", count: 21 },
    ],
    tenderTypeChart: [
      { type: "Feasibility", count: 15 },
      { type: "DPR", count: 22 },
      { type: "Feasibility Cum DPR", count: 12 },
      { type: "Authority Engineer", count: 8 },
      { type: "Independent Engineer", count: 10 },
      { type: "PMC", count: 25 },
      { type: "Supervision", count: 18 },
      { type: "EOI", count: 14 },
      { type: "Empanelment", count: 9 },
      { type: "O&M", count: 20 },
      { type: "IE O&M", count: 7 },
      { type: "AE O&M", count: 6 },
      { type: "Supervision O&M", count: 11 },
      { type: "Survey", count: 13 },
      { type: "Technology", count: 16 },
      { type: "Machinery", count: 19 },
      { type: "Slope Stability", count: 5 },
      { type: "Slope Protection", count: 4 },
      { type: "Detailed Design", count: 17 },
      { type: "Architecture", count: 12 },
      { type: "SAFETY CONSULTANT", count: 8 },
      { type: "PMS", count: 10 },
      { type: "Geotechnical", count: 9 },
      { type: "PSSA", count: 6 },
      { type: "GC", count: 14 },
      { type: "Temping", count: 7 },
      { type: "FLS", count: 5 },
      { type: "Station Redevelopment", count: 11 },
      { type: "Other", count: 20 },
    ],
    bgUpcoming: 41,
    bgCreated: 33,
    bgRequiredCount: 87,
  };

  // Fetch data from backend with Axios
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      let params = {};

      // ✅ If dates are selected → use custom range
      const today = new Date().toISOString().split("T")[0];
      console.log(today, "today");
      if (startDate && endDate && startDate !== today - 1) {
        params.from_date = startDate;
        params.to_date = endDate;
      }
      // ✅ If startDate is today OR no dates selected → use today filter
      else {
        params.filter = "today";
      }

      // ✅ Optional filters
      if (sectorFilter && sectorFilter !== "null") {
        params.sector = sectorFilter;
      }

      if (tenderTypeFilter && tenderTypeFilter !== "null") {
        params.tender_type = tenderTypeFilter;
      }

      if (stateFilter && stateFilter !== "null") {
        params.state = stateFilter;
      }
      const response = await axios.get(
        `${BASE_URL}/siteproject/biddashboard/`,
        { params },
      );

      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setDashboardData(HARDCODED_DUMMY_DATA);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, sectorFilter, tenderTypeFilter, stateFilter]);
  // Fetch data when date range or filters change
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Handle date change
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  // Stat Card component for counts
  const StatCard = ({ label, value, color }) => (
    <div className="stat-card-compact">
      <div className="stat-label-compact">{label}</div>
      <div className="stat-value-compact">{value}</div>
    </div>
  );

  // Pie Chart Component for EMD Status
  const PieChartComponent = ({ data, title, compact = false }) => {
    const total = data.reduce((sum, item) => sum + item.count, 0);
    const colors = ["#0b85d8", "#1e5ba8", "#0a6fc4"];

    // Calculate angles for pie slices
    let currentAngle = -90;
    const slices = data.map((item, idx) => {
      const percentage = (item.count / total) * 100;
      const angle = (percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      currentAngle = endAngle;
      return {
        ...item,
        percentage,
        startAngle,
        endAngle,
        color: colors[idx % colors.length],
      };
    });

    // SVG path generation for donut chart
    const radius = 80;
    const innerRadius = 55;
    const centerX = 120;
    const centerY = 120;

    const polarToCartesian = (angle) => {
      const rad = (angle * Math.PI) / 180;
      return {
        x: centerX + radius * Math.cos(rad),
        y: centerY + radius * Math.sin(rad),
      };
    };

    const innerPolarToCartesian = (angle) => {
      const rad = (angle * Math.PI) / 180;
      return {
        x: centerX + innerRadius * Math.cos(rad),
        y: centerY + innerRadius * Math.sin(rad),
      };
    };

    const getArcPath = (startAngle, endAngle) => {
      const start = polarToCartesian(startAngle);
      const end = polarToCartesian(endAngle);
      const innerStart = innerPolarToCartesian(startAngle);
      const innerEnd = innerPolarToCartesian(endAngle);
      const largeArc = endAngle - startAngle > 180 ? 1 : 0;

      return `
                M ${innerStart.x} ${innerStart.y}
                L ${start.x} ${start.y}
                A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}
                L ${innerEnd.x} ${innerEnd.y}
                A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}
            `;
    };

    return (
      <div
        className={compact ? "pie-compact-wrapper" : "chart-container"}
        style={!compact ? styles.container : {}}
      >
        {title && (
          <h3 className={compact ? "stat-column-title" : "chart-title"}>
            {title}
          </h3>
        )}
        <div
          className={compact ? "pie-container-compact" : "pie-container"}
          style={compact ? { gap: "12px" } : {}}
        >
          <div className="pie-chart-wrapper" style={styles.pieChartWrapper}>
            <svg
              width={compact ? "200" : "240"}
              height={compact ? "200" : "240"}
              className="pie-svg"
              style={styles.pieSvg}
            >
              {slices.map((slice, idx) => (
                <path
                  key={idx}
                  d={getArcPath(slice.startAngle, slice.endAngle)}
                  fill={slice.color}
                  className="pie-slice"
                  style={styles.pieSlice}
                />
              ))}
            </svg>
          </div>
          <div
            className={compact ? "pie-legend compact" : "pie-legend"}
            style={styles.pieLegend}
          >
            {slices.map((slice, idx) => (
              <div
                key={idx}
                className="pie-legend-item"
                style={styles.pieLegendItem}
              >
                <div
                  className="pie-legend-dot"
                  style={{
                    ...styles.pieLegendDot,
                    backgroundColor: slice.color,
                  }}
                ></div>
                <span
                  className="pie-legend-label"
                  style={styles.pieLegendLabel}
                >
                  {slice.label}
                </span>
                <span
                  className="pie-legend-value"
                  style={styles.pieLegendValue}
                >
                  {slice.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.dashboard}>
      {/* Header with Date Range Picker */}
      {/* <div style={styles.header}>
        <h1 style={styles.mainTitle}>BD Dashboard</h1>
        <div style={styles.dateRangeContainer}>
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            style={styles.dateButton}
          >
            {startDate} to {endDate}
          </button>
          {showDatePicker && (
            <div style={styles.datePickerPanel}>
              <div style={styles.dateInputGroup}>
                <label>From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  style={styles.dateInput}
                />
              </div>
              <div style={styles.dateInputGroup}>
                <label>To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  style={styles.dateInput}
                />
              </div>
              <button
                onClick={() => setShowDatePicker(false)}
                style={styles.closeDateBtn}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div> */}

      {/* Loading Indicator */}
      {loading && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingSpinner}></div>
          <span>Loading dashboard data...</span>
        </div>
      )}

      {/* Filter Row for Active Tenders Box */}
      <div className="filter-card">
        <div className="filter-bar">
          <span className="filter-label">Filters:</span>

          <div className="filter-group">
            <label className="filter-item-label">Sector:</label>

            <select
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">Select Sectors</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-item-label">Tender Type:</label>

            <select
              value={tenderTypeFilter}
              onChange={(e) => setTenderTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">Select Tender Type</option>
              <option value="Feasibility">Feasibility</option>
              <option value="DPR">DPR</option>
              <option value="FesibilityCumDPR">Feasibility Cum DPR</option>
              <option value="Authority_Engineer">Authority Engineer</option>
              <option value="Independent_Engineer">Independent Engineer</option>
              <option value="PMC">PMC</option>
              <option value="Supervision">Supervision</option>
              <option value="eoi">EOI</option>
              <option value="Empanelment">Empanelment</option>
              <option value="O&M">O&amp;M</option>
              <option value="IEO&M">IE O&amp;M</option>
              <option value="AE_O&M">AE O&amp;M</option>
              <option value="Supervision_O&M">Supervision O&amp;M</option>
              <option value="Survey">Survey</option>
              <option value="Technology">Technology</option>
              <option value="machinery">Machinery</option>
              <option value="slope_stability">Slope Stability</option>
              <option value="slope_protection">Slope Protection</option>
              <option value="Detailed_Design">Detailed Design</option>
              <option value="Architecture">Architecture</option>
              <option value="safety_consultant">SAFETY CONSULTANT</option>
              <option value="pms">PMS</option>
              <option value="geotechnical">Geotechnical</option>
              <option value="PSSA">PSSA</option>
              <option value="GC">GC</option>
              <option value="Temping">Temping</option>
              <option value="FLS">FLS</option>
              <option value="Station_Redevelopment">
                Station Redevelopment
              </option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-item-label">State:</label>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">Select State</option>
              {states.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.dateRangeContainer}>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              style={styles.dateButton}
            >
              {startDate} to {endDate}
            </button>
            {showDatePicker && (
              <div style={styles.datePickerPanel}>
                <div style={styles.dateInputGroup}>
                  <label>From:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    style={styles.dateInput}
                  />
                </div>
                <div style={styles.dateInputGroup}>
                  <label>To:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    style={styles.dateInput}
                  />
                </div>
                <button
                  onClick={() => setShowDatePicker(false)}
                  style={styles.closeDateBtn}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 1: Active | Inactive | EMD Status (3 columns) */}
      <div className="stats-row">
        {/* Active Tenders */}
        <div className="stat-column">
          <h3 className="stat-column-title">Active Tenders</h3>
          <div className="stat-grid-compact">
            <StatCard
              label="New"
              value={dashboardData.activeCounts.newTenders}
            />
            <StatCard
              label="Wish List"
              value={dashboardData.activeCounts.wishListTenders}
            />
            <StatCard
              label="Target"
              value={dashboardData.activeCounts.targetTenders}
            />
            <StatCard
              label="Final"
              value={dashboardData.activeCounts.finalTenders}
            />
            <StatCard
              label="Bid Prep"
              value={dashboardData.activeCounts.bidPreparation}
            />
            <StatCard
              label="Submitted"
              value={dashboardData.activeCounts.submittedTenders}
            />
          </div>
        </div>

        {/* Inactive Tenders */}
        <div className="stat-column">
          <h3 className="stat-column-title">Inactive Tenders</h3>
          <div className="stat-grid-compact">
            <StatCard
              label="Closed"
              value={dashboardData.inactiveCounts.closed}
            />
            <StatCard
              label="Expired"
              value={dashboardData.inactiveCounts.expired}
            />
          </div>
        </div>

        {/* EMD Status Pie Chart */}
        <div className="stat-column">
          <PieChartComponent
            data={[
              { label: "Upcoming", count: dashboardData.bgUpcoming },
              { label: "Created", count: dashboardData.bgCreated },
              { label: "Required", count: dashboardData.bgRequiredCount },
            ]}
            title="EMD Status"
            compact={true}
          />
        </div>
      </div>

      {/* Row 2: Tender by Sector (Full Width) */}
      <div className="chart-cards">
        <BarChartComponent
          data={dashboardData?.sectorChart}
          xKey="sector"
          yKey="count"
          title="Tenders by Sector"
        />
        <BarChartComponent
          data={dashboardData?.tenderTypeChart}
          xKey="tender_type"
          yKey="count"
          title="Tenders by Type"
        />
      </div>

      {/* Row 3: Tender by Type (Full Width) */}
      {/* <div className="chart-card">
        <BarChartComponent
          data={dashboardData?.tenderTypeChart}
          xKey="tender_type"
          yKey="count"
          title="Tenders by Type"
        />
      </div> */}

      <div style={styles.twoColumnGrid}>
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Emd Status</h2>
          <div style={styles.grid2}>
            <StatCard
              label="Emd Upcoming"
              value={dashboardData.bgUpcoming}
              color="#fff8e1"
            />
            <StatCard
              label="Emd Created"
              value={dashboardData.bgCreated}
              color="#e0f2f1"
            />
          </div>
        </div>
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Tenders Requiring Emd</h2>
          <StatCard
            label="Emd Required"
            value={dashboardData.bgRequiredCount}
            color="#fff8e1"
          />
          {/* <div style={styles.largeStatContainer}>
            <div style={styles.largeStatValue}>
              {dashboardData.bgRequiredCount}
            </div>
            <div style={styles.largeStatLabel}>Tenders</div>
            <div style={styles.largeStatSubtext}>Emd Required</div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

// --- Styles (Pure CSS, no Tailwind) - Professional Blue Theme ---
const styles = {
  dashboard: {
    padding: "10px",
    // backgroundColor: "#f8fafc",
    minHeight: "100vh",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    borderRadius: "0px",
  },
  header: {
    display: "flex",
    justifyContent: "flex-end",
    // justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "16px",
  },
  mainTitle: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#0b85d8",
    margin: 0,
    letterSpacing: "-0.5px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "600",
    color: "#1e293b",
    margin: 0,
  },
  dateRangeContainer: {
    position: "relative",
  },
  dateButton: {
    padding: "10px 20px",
    backgroundColor: "white",
    border: "2px solid #0b85d8",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    color: "#0b85d8",
    boxShadow: "0 2px 8px rgba(11, 133, 216, 0.1)",
    transition: "all 0.3s ease",
  },
  datePickerPanel: {
    position: "absolute",
    top: "48px",
    right: 0,
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "16px",
    boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)",
    zIndex: 100,
    display: "flex",
    gap: "12px",
    flexDirection: "column",
    minWidth: "260px",
  },
  dateInputGroup: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },
  dateInput: {
    padding: "6px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
  },
  closeDateBtn: {
    padding: "8px 16px",
    backgroundColor: "#0b85d8",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    marginTop: "4px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 8px rgba(11, 133, 216, 0.2)",
  },
  filterBar: {
    backgroundColor: "white",
    padding: "16px 20px",
    borderRadius: "12px",
    marginBottom: "24px",
    display: "flex",
    flexWrap: "wrap",
    gap: "24px",
    alignItems: "center",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    borderLeft: "4px solid #0b85d8",
  },
  filterLabel: {
    fontWeight: "700",
    color: "#0b85d8",
    display: "block",
    fontSize: "14px",
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  filterItemLabel: {
    fontSize: "14px",
    color: "#475569",
    fontWeight: "500",
  },
  filterSelect: {
    padding: "8px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    backgroundColor: "white",
    cursor: "pointer",
    fontWeight: "500",
    transition: "border-color 0.3s ease",
    color: "#1e293b",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    border: "1px solid #eef2f6",
  },
  cardTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#0b85d8",
    marginTop: 0,
    marginBottom: "24px",
    paddingBottom: "12px",
    borderBottom: "2px solid #e2e8f0",
    letterSpacing: "-0.2px",
  },
  grid6: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "16px",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
  },
  twoColumnGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
    gap: "24px",
    marginBottom: "24px",
  },
  twoColumnGridFlex: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
    marginBottom: "24px",
    width: "100%",
  },
  chartWrapper100: {
    width: "100%",
    minHeight: "550px",
    overflow: "hidden",
  },
  statCard: {
    padding: "20px",
    borderRadius: "12px",
    textAlign: "center",
    transition: "all 0.3s ease",
    cursor: "pointer",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
  },
  statLabel: {
    fontSize: "13px",
    color: "#475569",
    marginBottom: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#0b85d8",
    letterSpacing: "-0.5px",
  },
  chartContainer: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  chartTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1e293b",
    marginTop: 0,
    marginBottom: "20px",
    paddingBottom: "12px",
    borderBottom: "2px solid #e2e8f0",
  },
  chartWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  barRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    position: "relative",
  },
  barLabel: {
    width: "110px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#334155",
  },
  barTrack: {
    flex: 1,
    height: "32px",
    backgroundColor: "#f1f5f9",
    borderRadius: "8px",
    overflow: "hidden",
    position: "relative",
  },
  barFill: {
    height: "100%",
    borderRadius: "8px",
    transition: "width 0.3s ease",
    position: "relative",
  },
  barTooltip: {
    position: "absolute",
    top: "-30px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#1e293b",
    color: "white",
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "12px",
    whiteSpace: "nowrap",
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 0.2s",
    zIndex: 10,
  },
  barValue: {
    width: "45px",
    textAlign: "right",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
  },
  largeStatContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    backgroundColor: "#fef3c7",
    borderRadius: "12px",
    textAlign: "center",
  },
  largeStatValue: {
    fontSize: "48px",
    fontWeight: "800",
    color: "#d97706",
  },
  largeStatLabel: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#92400e",
    marginTop: "8px",
  },
  largeStatSubtext: {
    fontSize: "13px",
    color: "#b45309",
    marginTop: "4px",
  },
  loadingOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    flexDirection: "column",
    gap: "16px",
    color: "white",
  },
  loadingSpinner: {
    width: "40px",
    height: "40px",
    border: "4px solid rgba(255,255,255,0.3)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  pieContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "40px",
    padding: "20px",
  },
  pieChartWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "0 0 auto",
  },
  pieSvg: {
    filter: "drop-shadow(0 2px 8px rgba(0, 0, 0, 0.08))",
    transition: "transform 0.3s ease",
  },
  pieSlice: {
    stroke: "white",
    strokeWidth: "2",
    transition: "opacity 0.3s ease",
    cursor: "pointer",
  },
  pieLegend: {
    display: "flex",
    // flexDirection: "column",
    gap: "16px",
    flex: "0 0 auto",
  },
  pieLegendItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
    fontWeight: "500",
  },
  pieLegendDot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  pieLegendLabel: {
    color: "#475569",
    minWidth: "80px",
  },
  pieLegendValue: {
    color: "#0b85d8",
    fontWeight: "600",
    marginLeft: "auto",
  },
  container: {
    // padding: "25px",
    // background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
  },

  title: {
    marginBottom: "20px",
    fontSize: "22px",
    fontWeight: "600",
  },

  chartWrapper: {
    display: "flex",
    height: "380px",
  },

  yAxis: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    width: "50px",
    marginRight: "10px",
  },

  yTick: {
    display: "flex",
    alignItems: "center",
  },

  yLabel: {
    fontSize: "12px",
    color: "#666",
    width: "30px",
    textAlign: "right",
  },

  gridLine: {
    flex: 1,
    height: "1px",
    background: "#e5e7eb",
    marginLeft: "8px",
  },

  chart: {
    position: "relative",
    display: "flex",
    alignItems: "flex-end",
    gap: "20px",
    flex: 1,
    borderBottom: "2px solid #ccc",
    paddingBottom: "10px",
    overflowX: "auto",
  },
};

// Add keyframe animation to document
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .bar-row:hover .bar-tooltip {
    opacity: 1;
  }
`;
document.head.appendChild(styleSheet);

// Add hover effect for bar tooltips
const addHoverEffects = () => {
  const barRows = document.querySelectorAll('[style*="barRow"]');
  barRows.forEach((row) => {
    row.addEventListener("mouseenter", (e) => {
      const tooltip = row.querySelector('[style*="barTooltip"]');
      if (tooltip) tooltip.style.opacity = "1";
    });
    row.addEventListener("mouseleave", (e) => {
      const tooltip = row.querySelector('[style*="barTooltip"]');
      if (tooltip) tooltip.style.opacity = "0";
    });
  });
};

// Run hover effect after component mounts
setTimeout(addHoverEffects, 100);

export default BdDashboardinner;
